import React from 'react';
import { Link } from 'react-router-dom';
import { useT } from '../i18n';
import { currencySymbolOf } from '../constants/designer';
import { planOfShop } from '../constants/plans';
import PersonIcon from './PersonIcon';
import './ProductGrid.css';

// ── A GRID OF LISTINGS, AND ITS PAGER ────────────────────────────────────────
// ONE implementation for both places products are browsed: the landing
// catalogue a signed-out visitor sees, and the Products tab of /search. Those
// two started as separate markup and immediately began to drift — one paged
// and the other did not, one opened a product page and the other sprang a
// lightbox over the results — which is exactly the drift the rest of this
// codebase avoids by sharing (see onCommentWrite.js, bound twice; and
// DesignerProfile, one page for two roles).
//
// THEMED BY TOKEN, NOT BY PALETTE. Every colour here reads a `--site-*`
// variable, so the same component is dark-and-brass inside `.landing` — which
// redefines those tokens for its whole subtree — and follows the user's chosen
// theme everywhere else. The one thing the site tokens have no name for is an
// accent, so this declares `--pgd-accent` with the app's indigo as its default
// and the landing overrides it to gold.
//
// A CARD IS A LINK to /product/:id, which is what makes it openable in a new
// tab and pasteable to someone — a click handler could do neither. That is
// also why the byline is a SPAN rather than the OwnerBadge component used
// elsewhere: OwnerBadge renders a Link of its own, and an anchor inside an
// anchor is invalid HTML that browsers resolve unpredictably. The owner is
// passed in as data instead.

// The page size everywhere products are listed.
export const PRODUCT_PAGE = 12;
// How recently published still counts as new, for the corner badge.
const NEW_DAYS = 14;

const ms = (v) => (typeof v?.toMillis === 'function' ? v.toMillis() : 0);

/**
 * mixed / newest / cheapest / dearest. Returns a copy — never sorts in place.
 *
 * `tieBreak` is a Map of id → a random number drawn ONCE when the catalogue
 * loaded, and it is what makes 'mix' possible at all. Shuffling inside the
 * comparator would reorder the grid under the reader's cursor on every
 * keystroke, and a comparator that answers differently for the same pair is not
 * a valid ordering — the engine is entitled to produce nonsense from it. A
 * pre-drawn key gives an order that is random per visit and fixed within it.
 *
 * Falls back to newest when 'mix' is asked for without a map, so a caller that
 * has no shuffle to offer gets a defined order rather than the input order.
 */
export function sortProducts(rows, sort, tieBreak) {
  const out = [...rows];
  if (sort === 'cheap') out.sort((a, b) => (a.price ?? Infinity) - (b.price ?? Infinity));
  else if (sort === 'dear') out.sort((a, b) => (b.price ?? -Infinity) - (a.price ?? -Infinity));
  else if (sort === 'mix' && tieBreak) out.sort((a, b) => (tieBreak.get(a.id) ?? 0) - (tieBreak.get(b.id) ?? 0));
  else out.sort((a, b) => ms(b.createdAt) - ms(a.createdAt));
  return out;
}

/**
 * Which page numbers to draw: always the first and the last, always the
 * current and its neighbours, and an ellipsis for whatever that skips — so the
 * row stays the same width whether there are three pages or three hundred.
 */
export function pageNumbers(at, pageCount) {
  const out = [];
  for (let n = 1; n <= pageCount; n++) {
    if (n === 1 || n === pageCount || Math.abs(n - at) <= 1) out.push(n);
    else if (out[out.length - 1] !== '…') out.push('…');
  }
  return out;
}

/**
 * The grid.
 *
 * `ownerOf(uid)` returns `{ name, avatarUrl }` or nothing — the caller already
 * holds a directory of shops in both cases, so resolving it per tile here
 * would be a lookup the page has already done.
 *
 * `onNavigate` fires just before leaving, for the caller to record where the
 * reader was standing (see useRestoreScroll).
 */
export default function ProductGrid({ items, ownerOf, onNavigate }) {
  const { t } = useT();

  return (
    <div className="pgd-grid">
      {items.map(p => {
        const owner = ownerOf?.(p.ownerUid);
        const isNew = Date.now() - ms(p.createdAt) < NEW_DAYS * 864e5;
        // ── THE OWNER'S TIER, WORN AS A BORDER ─────────────────────────────
        // A listing has no plan of its own; the shop selling it does. An owner
        // the directory has not resolved yet — or a caller that passes no
        // `ownerOf` at all — lands on free and gets the ordinary border, which
        // is the right way to fail: a tile can only ever be promoted by a plan
        // that was actually read, never by a missing lookup.
        //
        // BORDER ONLY, no wash over the card. The designer cards can carry a
        // gold gradient because they are mostly text; a product tile is mostly
        // photograph, and tinting it would misrepresent the thing being sold.
        const tier = planOfShop(owner).id;
        return (
          <Link
            key={p.id}
            className={`pgd-card pgd-card--${tier}`}
            to={`/product/${p.id}`}
            onClick={onNavigate}
          >
            <span className="pgd-thumb">
              {p.thumbUrl
                ? <img src={p.thumbUrl} alt="" loading="lazy" />
                : <span className="pgd-thumb-empty" />}
              {isNew && <span className="pgd-badge">{t('lnd_badge_new')}</span>}
            </span>
            {/* Only when the owner is known. A byline reading "—" while a
                directory loads is worse than the line appearing a moment
                later. */}
            {owner?.name && (
              <span className="pgd-by">
                <span className="pgd-avatar">
                  {owner.avatarUrl ? <img src={owner.avatarUrl} alt="" /> : <PersonIcon />}
                </span>
                <span className="pgd-shop">{owner.name}</span>
              </span>
            )}
            <span className="pgd-title">{p.title || p.description || '—'}</span>
            {p.price != null && (
              <span className="pgd-price">
                {p.price.toLocaleString()} {currencySymbolOf(p.currency)}
              </span>
            )}
          </Link>
        );
      })}
    </div>
  );
}

/**
 * Pages of twelve, rather than a growing list.
 *
 * "Show more" made the page taller and taller with no way to say where you
 * were in it — a reader who opened a listing from the fourth batch and came
 * back had to press it three times to find their way there again. A page
 * number is a place; a scroll depth is not.
 *
 * Renders nothing on a single page: a pager with one button in it is
 * furniture.
 */
export function ProductPager({ page, pageCount, onPage }) {
  const { t } = useT();
  if (pageCount <= 1) return null;

  return (
    <nav className="pgd-pager" aria-label={t('lnd_pages')}>
      <button
        type="button"
        className="pgd-page pgd-page--step"
        onClick={() => onPage(page - 1)}
        disabled={page === 1}
        aria-label={t('lnd_prev')}
      >‹</button>

      {pageNumbers(page, pageCount).map((n, i) => (n === '…' ? (
        <span key={`gap${i}`} className="pgd-gap" aria-hidden="true">…</span>
      ) : (
        <button
          key={n}
          type="button"
          className={`pgd-page${n === page ? ' pgd-page--on' : ''}`}
          onClick={() => onPage(n)}
          aria-current={n === page ? 'page' : undefined}
        >{n}</button>
      )))}

      <button
        type="button"
        className="pgd-page pgd-page--step"
        onClick={() => onPage(page + 1)}
        disabled={page === pageCount}
        aria-label={t('lnd_next')}
      >›</button>
    </nav>
  );
}
